import * as jose from "jose";
import { LogLevel, HubConnectionBuilder, HubConnection } from "@microsoft/signalr";

// forked from @aaronpowell/graphql-signalr-subscriptions 0.3.0 because the @microsoft/signalr import was wrong
export class SignalRClient {
  private static defaultHubName = "DocumentGenerationHub";
  private connection: HubConnection;

  constructor(
    private endpoint: string,
    private accessKey: string,
    private hubName: string,
    logLevel: LogLevel = LogLevel.Information,
  ) {
    this.connection = new HubConnectionBuilder()
      .withUrl(`${endpoint}/client/?hub=${hubName}`, {
        accessTokenFactory: async () => {
          const payload: JwtPayload = {
            aud: `${endpoint}/client/?hub=${hubName}`,
            iat: Math.floor(Date.now() / 1000) - 30,
            exp: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
          };

          const secret = new TextEncoder().encode(this.accessKey);
          const accessToken = await new jose.SignJWT({ ...payload } as jose.JWTPayload).setProtectedHeader({ alg: "HS256" }).sign(secret);

          return accessToken;
        },
      })
      .configureLogging(logLevel)
      .build();
  }

  static fromConnectionString(
    connectionString?: string,
    hubName: string = this.defaultHubName,
    logLevel: LogLevel = LogLevel.Information,
  ): SignalRClient {
    if (!connectionString) {
      throw new Error("No SignalR Service connection string found.");
    }

    const endpointMatch = /\bEndpoint=([^;]+)/i.exec(connectionString);
    const endpoint = endpointMatch?.[1];
    const accessKeyMatch = /\bAccessKey=([^;]+)/i.exec(connectionString);
    const accessKey = accessKeyMatch?.[1];

    if (!(endpoint && accessKey)) {
      throw new Error("Could not parse SignalR Service connection string.");
    }

    return new SignalRClient(endpoint, accessKey, hubName, logLevel);
  }

  public async send(eventName: string, eventData?: unknown): Promise<void> {
    const hubUrl = `${this.endpoint}/api/v1/hubs/${this.hubName}`;
    const accessToken = await this.generateAccessToken(hubUrl);

    const payload: SendPayload = {
      target: eventName,
      arguments: [eventData],
    };

    await fetch(hubUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });
  }

  public async start() {
    return this.connection.start();
  }

  public on(triggerName: string, handler: (...args: unknown[]) => void) {
    this.connection.on(triggerName, handler);
  }

  public off(triggerName: string, handler: (...args: unknown[]) => void) {
    this.connection.off(triggerName, handler);
  }

  async generateNegotiatePayload(userId?: string): Promise<NegotiatePayload> {
    const hubUrl = `${this.endpoint}/client/?hub=${this.hubName}`;
    const accessToken = await this.generateAccessToken(hubUrl, userId);
    return {
      accessToken,
      url: hubUrl,
    };
  }

  private async generateAccessToken(url: string, userId?: string) {
    const payload: JwtPayload = {
      aud: url,
      iat: Math.floor(Date.now() / 1000) - 30,
      exp: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
    };

    if (userId) {
      payload.nameid = userId;
    }

    const secret = new TextEncoder().encode(this.accessKey);
    const accessToken = await new jose.SignJWT({ ...payload } as jose.JWTPayload).setProtectedHeader({ alg: "HS256" }).sign(secret);
    return accessToken;
  }
}

interface JwtPayload {
  aud: string;
  iat: number;
  exp: number;
  nameid?: string;
}

interface NegotiatePayload {
  accessToken: string;
  url: string;
}

interface SendPayload {
  target: string;
  arguments: unknown[];
  userId?: string;
  groupName?: string;
}
